import moment from "moment";
import {
  INHERITANCE_TESTATOR_BASIS_START_DATE,
  PRE_1997_CONSTRUCTION_CUTOFF_DATE
} from "../../shared/haklaiConstants";
import {
  getHaklaiSeededCostByLabel,
  HAKLAI_PRE_CALC_LABELS
} from "../haklaiPreCalcTexts";

export const shouldUseHaklaiTestatorBoughtPropertyDate = (testatorPassingDate) => {
  const testatorPassingDateMoment = testatorPassingDate ? moment(testatorPassingDate) : null;
  return Boolean(
    testatorPassingDateMoment
    && testatorPassingDateMoment.isValid()
    && testatorPassingDateMoment.isSameOrAfter(moment(INHERITANCE_TESTATOR_BASIS_START_DATE), "day")
  );
};

// ── Acquisition-details form shape (pure derivation) ──────────────────────
// Given the preCalc state, returns the shape of the acquisition section:
// which date inputs to render, whether the amount field is shown, and the
// amount label to use. Inheritance case branches on the 1981 estate-tax
// boundary because pre-1981 inheritances use the passing-date value for
// cost basis (no testator purchase date / adjusted label).
export const getHaklaiAcquisitionDetailsConfig = ({
  wayOfGettingProperty,
  acquisitionDetails = {}
} = {}) => {
  const testatorPassingDate = acquisitionDetails.testatorPassingDate;
  const isTestatorPassingOnOrAfterBasisStart = shouldUseHaklaiTestatorBoughtPropertyDate(testatorPassingDate);

  if (wayOfGettingProperty === "purchase") {
    return {
      dateFields: [
        {
          key: "deduDate",
          label: HAKLAI_PRE_CALC_LABELS.purchaseDate,
          name: "deduDate",
          value: acquisitionDetails.deduDate || ""
        }
      ],
      showAmount: true,
      amountLabel: HAKLAI_PRE_CALC_LABELS.purchaseAmountOriginalCurrency
    };
  }

  if (wayOfGettingProperty === "inheritance") {
    return {
      dateFields: [
        {
          key: "testatorPassingDate",
          label: HAKLAI_PRE_CALC_LABELS.testatorPassingDate,
          name: "testatorPassingDate",
          value: testatorPassingDate || ""
        },
        ...(isTestatorPassingOnOrAfterBasisStart
          ? [
              {
                key: "testatorBoughtPropertyDate",
                label: HAKLAI_PRE_CALC_LABELS.testatorPurchaseDate,
                name: "testatorBoughtPropertyDate",
                value: acquisitionDetails.testatorBoughtPropertyDate || ""
              }
            ]
          : [])
      ],
      showAmount: Boolean(testatorPassingDate),
      amountLabel: isTestatorPassingOnOrAfterBasisStart
        ? HAKLAI_PRE_CALC_LABELS.testatorPurchaseAmountOriginalCurrency
        : HAKLAI_PRE_CALC_LABELS.testatorPassingValueOriginalCurrency
    };
  }

  if (wayOfGettingProperty === "gift") {
    return {
      dateFields: [
        {
          key: "deduDate",
          label: HAKLAI_PRE_CALC_LABELS.giftDonorPurchaseDate,
          name: "deduDate",
          value: acquisitionDetails.deduDate || ""
        }
      ],
      showAmount: true,
      amountLabel: HAKLAI_PRE_CALC_LABELS.giftDonorPurchaseAmountOriginalCurrency
    };
  }

  return null;
};

export const getHaklaiOwnershipStartDate = (preCalc = {}) => {
  const acquisitionDetails = preCalc?.acquisitionDetails || {};
  if (preCalc?.wayOfGettingProperty === "inheritance") {
    return acquisitionDetails.testatorPassingDate || "";
  }
  return acquisitionDetails.deduDate || "";
};

// Post-1981 inheritance displays the testator's purchase date because it is
// the date the cost basis originated, not the date of passing.
export const getHaklaiAcquisitionDisplayDate = (preCalc = {}) => {
  const acquisitionDetails = preCalc?.acquisitionDetails || {};
  if (preCalc?.wayOfGettingProperty === "inheritance") {
    const passingDate = acquisitionDetails.testatorPassingDate || "";
    if (shouldUseHaklaiTestatorBoughtPropertyDate(passingDate)) {
      return acquisitionDetails.testatorBoughtPropertyDate || "";
    }
    return passingDate;
  }
  return acquisitionDetails.deduDate || "";
};

// Purchase-row depreciation follows the acquisition basis date. This differs
// from ordinary expense validation for post-1981 inheritance: the former may
// use the testator's purchase date; the latter starts with the heir's ownership.
export const getHaklaiPurchaseDepreciationStartDate = (preCalc = {}) => (
  getHaklaiAcquisitionDisplayDate(preCalc)
);

export const isHaklaiPre1997AcquisitionDateEligible = (preCalc = {}) => {
  const acquisitionDate = getHaklaiAcquisitionDisplayDate(preCalc);
  const acquisitionMoment = acquisitionDate ? moment(acquisitionDate) : null;
  return Boolean(
    acquisitionMoment
    && acquisitionMoment.isValid()
    && acquisitionMoment.isBefore(moment(PRE_1997_CONSTRUCTION_CUTOFF_DATE), "day")
  );
};

export const getHaklaiSeededExpenseTypeLabel = (preCalc = {}) => {
  if (preCalc?.wayOfGettingProperty === "inheritance") {
    return getHaklaiSeededCostByLabel(HAKLAI_PRE_CALC_LABELS.testator);
  }
  if (preCalc?.wayOfGettingProperty === "gift") {
    return getHaklaiSeededCostByLabel(HAKLAI_PRE_CALC_LABELS.giftDonor);
  }
  return HAKLAI_PRE_CALC_LABELS.partialAcquisitionCost;
};
